import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Download, ExternalLink, RefreshCw } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  ExportDrawer,
  FullscreenButton,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
  useFullscreen,
} from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { useReorderAlerts, useVendorPOs } from '../api/queries'
import type { ReorderAlertRow, VendorPORow } from '../api/types'
import { FreightStatusCell } from '../components/FreightStatusCell'
import { POStatusCell } from '../components/POStatusCell'
import { ShipConfirmationCell } from '../components/ShipConfirmationCell'
import { isOverduePO } from '../utils/overdue'
import {
  PROCUREMENT_ALERT_COLUMNS,
  PROCUREMENT_PO_COLUMNS,
} from '../utils/csv'

type VendorFilter = 'all' | string
type ProcView = 'pos' | 'reorder'

export function ProcurementPage() {
  const { data, isLoading, isFetching, isError, error, refetch } = useVendorPOs()
  const reorderQuery = useReorderAlerts()
  const [view, setView] = useState<ProcView>('pos')
  const navigate = useNavigate()

  const openFreightPipeline = useCallback(
    (pipelineId: number) => {
      navigate({ to: '/logistics/$pipelineId', params: { pipelineId: String(pipelineId) } })
    },
    [navigate],
  )
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [vendorFilter, setVendorFilter] = useState<VendorFilter>('all')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [belowMinOnly, setBelowMinOnly] = useState(true)
  const { fullscreen, toggle: toggleFullscreen } = useFullscreen()
  const [exportOpen, setExportOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const rows = useMemo<VendorPORow[]>(() => data?.rows ?? [], [data])

  const vendorOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.vendor).filter(Boolean))
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [rows])

  const filtered = useMemo<VendorPORow[]>(() => {
    const needle = debounced.trim().toLowerCase()
    return rows.filter((r) => {
      if (overdueOnly && !isOverduePO(r)) return false
      if (vendorFilter !== 'all' && r.vendor !== vendorFilter) return false
      if (!needle) return true
      return (
        r.po_number.toLowerCase().includes(needle) ||
        r.vendor.toLowerCase().includes(needle) ||
        r.product_name.toLowerCase().includes(needle)
      )
    })
  }, [rows, debounced, vendorFilter, overdueOnly])

  const overdueCount = useMemo(
    () => rows.filter((r) => isOverduePO(r)).length,
    [rows],
  )
  const totalOpen = useMemo(
    () => filtered.reduce((sum, r) => sum + (r.line_total ?? 0), 0),
    [filtered],
  )

  // Reorder alerts — served by /api/reports/procurement alongside the PO
  // report. Plucked from `reorder_alerts.rules`; below_min filter is
  // client-side so toggling feels instant.
  const reorderAllRows = useMemo<ReorderAlertRow[]>(
    () => reorderQuery.data?.reorder_alerts?.rules ?? [],
    [reorderQuery.data],
  )
  const reorderFiltered = useMemo<ReorderAlertRow[]>(() => {
    const needle = debounced.trim().toLowerCase()
    return reorderAllRows.filter((r) => {
      if (belowMinOnly && !r.below_min) return false
      if (!needle) return true
      return (
        r.product.toLowerCase().includes(needle) ||
        (r.warehouse ?? '').toLowerCase().includes(needle)
      )
    })
  }, [reorderAllRows, debounced, belowMinOnly])
  const belowMinCount = useMemo(
    () => reorderAllRows.filter((r) => r.below_min).length,
    [reorderAllRows],
  )

  const columns = useMemo<ColumnDef<VendorPORow, unknown>[]>(
    () => [
      {
        id: 'po_number',
        header: 'PO #',
        accessorKey: 'po_number',
        cell: (ctx) => (
          <a
            href={odooUrl('purchase.order', ctx.row.original.po_id)}
            target="_blank"
            rel="noreferrer"
            className="text-link hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {ctx.row.original.po_number}
          </a>
        ),
      },
      { id: 'po_ln', header: 'PO:LN', accessorKey: 'po_ln' },
      { id: 'vendor', header: 'Vendor', accessorKey: 'vendor' },
      { id: 'po_date', header: 'PO Date', accessorKey: 'po_date' },
      { id: 'po_confirmed', header: 'Confirmed', accessorKey: 'po_confirmed' },
      {
        id: 'expected_receipt',
        header: 'Expected',
        accessorKey: 'expected_receipt',
      },
      {
        id: 'revised_ship_date',
        header: 'Revised Ship Date',
        cell: (ctx) => <ShipConfirmationCell row={ctx.row.original} />,
      },
      {
        id: 'line',
        header: 'Line',
        accessorKey: 'line',
        meta: { numeric: true },
      },
      { id: 'product_name', header: 'Product', accessorKey: 'product_name' },
      { id: 'uom', header: 'UoM', accessorKey: 'uom' },
      {
        id: 'qty_ordered',
        header: 'Ordered',
        accessorKey: 'qty_ordered',
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'qty_received',
        header: 'Received',
        accessorKey: 'qty_received',
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'qty_open',
        header: 'Open',
        accessorKey: 'qty_open',
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'unit_cost',
        header: 'Unit cost',
        accessorKey: 'unit_cost',
        cell: (ctx) =>
          (ctx.getValue() as number).toLocaleString(undefined, {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 4,
          }),
      },
      {
        id: 'line_total',
        header: 'Line total',
        accessorKey: 'line_total',
        cell: (ctx) =>
          (ctx.getValue() as number).toLocaleString(undefined, {
            style: 'currency',
            currency: 'USD',
          }),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (ctx) => <POStatusCell row={ctx.row.original} />,
      },
      {
        id: 'freight',
        header: 'Freight',
        cell: (ctx) => (
          <FreightStatusCell row={ctx.row.original} onCreated={openFreightPipeline} />
        ),
      },
    ],
    [openFreightPipeline],
  )

  const reorderColumns = useMemo<ColumnDef<ReorderAlertRow, unknown>[]>(
    () => [
      {
        id: 'product',
        header: 'Product',
        accessorKey: 'product',
        cell: (ctx) => (
          <a
            href={odooUrl('product.product', ctx.row.original.product_id)}
            target="_blank"
            rel="noreferrer"
            className="text-link hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {ctx.row.original.product}
          </a>
        ),
      },
      {
        id: 'warehouse',
        header: 'Warehouse',
        accessorKey: 'warehouse',
        cell: (ctx) => (ctx.getValue() as string | null) ?? '—',
      },
      {
        id: 'qty_on_hand',
        header: 'On hand',
        accessorKey: 'qty_on_hand',
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'min_qty',
        header: 'Min',
        accessorKey: 'min_qty',
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'max_qty',
        header: 'Max',
        accessorKey: 'max_qty',
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'qty_forecast',
        header: 'Forecast',
        accessorKey: 'qty_forecast',
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'below_min',
        header: 'Status',
        cell: (ctx) =>
          ctx.row.original.below_min ? (
            <Chip size="xs" variant="danger">
              <AlertTriangle size={10} /> below min
            </Chip>
          ) : (
            <Chip size="xs" variant="neutral">
              ok
            </Chip>
          ),
      },
    ],
    [],
  )

  // `/` focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const onPosView = view === 'pos'
  const loading = onPosView
    ? isLoading
    : reorderQuery.isLoading
  const fetching = onPosView
    ? isFetching
    : reorderQuery.isFetching
  const currentCount = onPosView ? filtered.length : reorderFiltered.length
  const totalCount = onPosView ? rows.length : reorderAllRows.length

  return (
    <div
      data-density="compact"
      className={
        fullscreen
          ? 'fixed inset-0 z-50 flex flex-col bg-canvas'
          : 'flex h-full flex-col'
      }
    >
      <PageHeaderBand
        title={onPosView ? 'Open Vendor POs' : 'Reorder Alerts'}
        description={
          onPosView
            ? 'Open purchase-order lines across all vendors. Overdue rows get a red left-border accent.'
            : 'Products at or below their replenishment minimum. Signal for purchasing to cut new POs.'
        }
      >
        <MetricStrip variant="inline" className="ml-auto">
          {onPosView ? (
            <>
              <MetricCard variant="inline" label="Open lines" value={rows.length.toLocaleString()} />
              <MetricCard
                variant="inline"
                label="Overdue"
                value={overdueCount.toLocaleString()}
                valueTone={overdueCount > 0 ? 'danger' : 'default'}
              />
              <MetricCard variant="inline" label="Vendors" value={vendorOptions.length.toLocaleString()} />
              <MetricCard
                variant="inline"
                label="Open $"
                value={totalOpen.toLocaleString(undefined, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                })}
              />
            </>
          ) : (
            <>
              <MetricCard
                variant="inline"
                label="Total rules"
                value={reorderAllRows.length.toLocaleString()}
              />
              <MetricCard
                variant="inline"
                label="Below min"
                value={belowMinCount.toLocaleString()}
                valueTone={belowMinCount > 0 ? 'danger' : 'default'}
              />
            </>
          )}
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder={
              onPosView
                ? 'Search PO, vendor, product…'
                : 'Search product, warehouse…'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <ViewToggle
              aria-label="View"
              value={view}
              onChange={setView}
              options={[
                { value: 'pos', label: 'Open POs' },
                { value: 'reorder', label: 'Reorder Alerts' },
              ]}
            />
            {onPosView ? (
              <>
                <ToolbarDivider />
                <Select
                  aria-label="Vendor filter"
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value as VendorFilter)}
                >
                  <option value="all">All vendors</option>
                  {vendorOptions.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
                <ToolbarDivider />
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-primary">
                  <input
                    type="checkbox"
                    checked={overdueOnly}
                    onChange={(e) => setOverdueOnly(e.target.checked)}
                    className="cursor-pointer"
                  />
                  Overdue only
                </label>
              </>
            ) : (
              <>
                <ToolbarDivider />
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-primary">
                  <input
                    type="checkbox"
                    checked={belowMinOnly}
                    onChange={(e) => setBelowMinOnly(e.target.checked)}
                    className="cursor-pointer"
                  />
                  Below min only
                </label>
              </>
            )}
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>
              {currentCount.toLocaleString()} of {totalCount.toLocaleString()}
            </ToolbarCount>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={fetching && !loading}
              onClick={() => (onPosView ? refetch() : reorderQuery.refetch())}
            >
              <RefreshCw size={14} />
            </Button>
            <FullscreenButton fullscreen={fullscreen} onToggle={toggleFullscreen} />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setExportOpen(true)}
              disabled={currentCount === 0}
              title="Pick columns or apply a saved template before download"
            >
              <Download size={12} /> Export…
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-hidden bg-canvas p-3">
          {onPosView ? (
            <DataTable
              data={filtered}
              columns={columns}
              loading={isLoading}
              error={isError ? error : undefined}
              onRetry={() => void refetch()}
              errorSubject="vendor POs"
              criticalRowPredicate={isOverduePO}
              getRowId={(r) => String(r.line_id)}
              moduleId="procurement-open-pos"
              onRowClick={(r) =>
                window.open(odooUrl('purchase.order', r.po_id), '_blank')
              }
              maxHeight="100%"
              columnWidths={{
                po_number: 120,
                po_date: 110,
                po_confirmed: 110,
                expected_receipt: 110,
                uom: 60,
                qty_ordered: 90,
                qty_received: 90,
                qty_open: 90,
                unit_cost: 110,
                line_total: 110,
                status: 110,
              }}
              minTableWidth={1500}
              emptyState={
                <EmptyState
                  variant="no-match"
                  title="No PO lines match"
                  description="Try clearing the vendor filter or 'Overdue only'."
                  action={
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSearch('')
                        setVendorFilter('all')
                        setOverdueOnly(false)
                      }}
                    >
                      Reset
                    </Button>
                  }
                />
              }
            />
          ) : (
            <DataTable
              data={reorderFiltered}
              columns={reorderColumns}
              loading={reorderQuery.isLoading}
              error={reorderQuery.isError ? reorderQuery.error : undefined}
              onRetry={() => void reorderQuery.refetch()}
              errorSubject="reorder alerts"
              criticalRowPredicate={(r) => r.below_min}
              getRowId={(r) => String(r.id)}
              moduleId="procurement-reorder-alerts"
              onRowClick={(r) =>
                window.open(odooUrl('product.product', r.product_id), '_blank')
              }
              maxHeight="100%"
              columnWidths={{
                product: 280,
                warehouse: 160,
                qty_on_hand: 100,
                min_qty: 80,
                max_qty: 80,
                qty_forecast: 100,
                below_min: 130,
              }}
              minTableWidth={1000}
              emptyState={
                <EmptyState
                  variant="no-match"
                  title={
                    reorderAllRows.length === 0
                      ? 'No reorder rules found'
                      : 'No matching reorder alerts'
                  }
                  description={
                    reorderAllRows.length === 0
                      ? 'No active `stock.warehouse.orderpoint` records exist. Add one in Odoo to start tracking reorder points.'
                      : belowMinOnly
                        ? 'Everything is at or above minimum. Untoggle "Below min only" to see all rules.'
                        : 'Try clearing the search.'
                  }
                  action={
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSearch('')
                        setBelowMinOnly(true)
                      }}
                    >
                      Reset
                    </Button>
                  }
                />
              }
            />
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-border-subtle bg-surface-sunk px-3 py-1.5 text-2xs text-secondary">
          <span>
            Click a row to <ExternalLink size={10} className="inline" /> open in Odoo
          </span>
          <span>/ focus search</span>
        </footer>
      </div>

      {onPosView ? (
        <ExportDrawer
          open={exportOpen}
          onOpenChange={setExportOpen}
          rows={filtered}
          columns={PROCUREMENT_PO_COLUMNS}
          moduleId="procurement-pos"
          filenameBase="spi-procurement-open-pos"
        />
      ) : (
        <ExportDrawer
          open={exportOpen}
          onOpenChange={setExportOpen}
          rows={reorderFiltered}
          columns={PROCUREMENT_ALERT_COLUMNS}
          moduleId="procurement-alerts"
          filenameBase="spi-procurement-reorder-alerts"
        />
      )}
    </div>
  )
}
